import { useState } from 'react';
import { ChevronDown, ChevronRight, HelpCircle, Mail, Shield, Settings, CreditCard } from 'lucide-react';
import DashboardLayout from './DashboardLayout';
import { useTranslation } from 'react-i18next';

const HelpPage = () => {
  const { t } = useTranslation();
  const [expandedSections, setExpandedSections] = useState<string[]>(['getting-started']);
  const [isPlanModalOpen, setIsPlanModalOpen] = useState(false);

  const toggleSection = (sectionId: string) => {
    setExpandedSections(prev => 
      prev.includes(sectionId) 
        ? prev.filter(id => id !== sectionId)
        : [...prev, sectionId]
    );
  };

  const helpSections = [
    {
      id: 'getting-started',
      icon: <Mail className="h-5 w-5 text-blue-600" />,
      title: t('help.sections.gettingStarted.title'),
      questions: [
        {
          question: t('help.sections.gettingStarted.questions.0.question'),
          answer: t('help.sections.gettingStarted.questions.0.answer')
        },
        {
          question: t('help.sections.gettingStarted.questions.1.question'),
          answer: t('help.sections.gettingStarted.questions.1.answer')
        },
        {
          question: t('help.sections.gettingStarted.questions.2.question'),
          answer: t('help.sections.gettingStarted.questions.2.answer')
        }
      ]
    },
    {
      id: 'aliases',
      icon: <Shield className="h-5 w-5 text-green-600" />,
      title: t('help.sections.aliases.title'),
      questions: [
        {
          question: t('help.sections.aliases.questions.0.question'),
          answer: t('help.sections.aliases.questions.0.answer')
        },
        {
          question: t('help.sections.aliases.questions.1.question'),
          answer: t('help.sections.aliases.questions.1.answer')
        },
        {
          question: t('help.sections.aliases.questions.2.question'),
          answer: t('help.sections.aliases.questions.2.answer')
        },
        {
          question: t('help.sections.aliases.questions.3.question'),
          answer: t('help.sections.aliases.questions.3.answer')
        }
      ]
    },
    {
      id: 'settings',
      icon: <Settings className="h-5 w-5 text-purple-600" />,
      title: t('help.sections.settings.title'),
      questions: [
        {
          question: t('help.sections.settings.questions.0.question'),
          answer: t('help.sections.settings.questions.0.answer')
        },
        {
          question: t('help.sections.settings.questions.1.question'),
          answer: t('help.sections.settings.questions.1.answer')
        },
        {
          question: t('help.sections.settings.questions.2.question'),
          answer: t('help.sections.settings.questions.2.answer')
        }
      ]
    },
    {
      id: 'billing',
      icon: <CreditCard className="h-5 w-5 text-yellow-600" />,
      title: t('help.sections.billing.title'),
      questions: [
        {
          question: t('help.sections.billing.questions.0.question'),
          answer: t('help.sections.billing.questions.0.answer')
        },
        {
          question: t('help.sections.billing.questions.1.question'),
          answer: t('help.sections.billing.questions.1.answer')
        },
        {
          question: t('help.sections.billing.questions.2.question'),
          answer: t('help.sections.billing.questions.2.answer')
        }
      ]
    }
  ];

  return (
    <DashboardLayout
      isPlanModalOpen={isPlanModalOpen}
      onPlanModalClose={() => setIsPlanModalOpen(false)}
      onOpenPlanModal={() => setIsPlanModalOpen(true)}
    >
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-xl shadow-sm">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center">
              <HelpCircle className="h-8 w-8 text-blue-600 mr-3" />
              <div>
                <h1 className="text-2xl font-semibold text-gray-900">
                  {t('help.title')}
                </h1>
                <p className="text-gray-600 mt-1">
                  {t('help.subtitle')}
                </p>
              </div>
            </div>
          </div>

          <div className="p-6">
            <div className="space-y-6">
              {helpSections.map((section) => (
                <div key={section.id} className="border border-gray-200 rounded-lg">
                  <button
                    onClick={() => toggleSection(section.id)}
                    className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center">
                      {section.icon}
                      <h2 className="text-lg font-medium text-gray-900 ml-3">
                        {section.title}
                      </h2>
                    </div>
                    {expandedSections.includes(section.id) ? (
                      <ChevronDown className="h-5 w-5 text-gray-500" />
                    ) : (
                      <ChevronRight className="h-5 w-5 text-gray-500" />
                    )}
                  </button>
                  
                  {expandedSections.includes(section.id) && (
                    <div className="border-t border-gray-200 p-4 space-y-4">
                      {section.questions.map((qa, index) => (
                        <div key={index} className="space-y-2">
                          <h3 className="font-medium text-gray-900">
                            {qa.question}
                          </h3>
                          <p className="text-gray-700 leading-relaxed">
                            {qa.answer}
                          </p>
                          {index < section.questions.length - 1 && (
                            <hr className="border-gray-100" />
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="mt-8 p-6 bg-blue-50 rounded-lg">
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {t('help.contact.title')}
              </h3>
              <p className="text-gray-700 mb-4">
                {t('help.contact.description')}
              </p>
              <a
                href="https://commonapi.mxfast.com/static/feedback/?app=uumail"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              >
                <Mail className="h-4 w-4 mr-2" />
                {t('help.contact.button')}
              </a>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default HelpPage;